import { faEllipsisVertical, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons/faArrowRight";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    useTheme,
} from "@vapor/react-material";
import dayjs from "dayjs";
import { type ReactElement, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { createSearchParams, useNavigate } from "react-router-dom";
import { FulfilmentStatus } from "../../../../../core/api/use-get-fulfilment-detail/FulfilmentStatus";
import type { TaxPayerDetailsDto } from "../../../../../core/api/use-get-fulfilment-tax-payers/TaxPayerDetailsDto";
import { TaxPayerSource } from "../../../../../core/api/use-get-fulfilment-tax-payers/TaxPayerSource";
import type { FulfilmentDto } from "../../../../../core/api/use-get-fulfilments/FulfilmentDto";
// Temporarily unused: only needed by the hidden FulfilmentOperatorAvatar below.
// import { useGetOperators } from "../../../../../core/api/use-get-operators/useGetOperators";
import { useLinkTaxPayerToFulfilment } from "../../../../../core/api/use-link-tax-payer-to-fulfilment/useLinkTaxPayerToFulfilment";
import { useUnlinkTaxPayerFromFulfilment } from "../../../../../core/api/use-unlink-tax-payer-from-fulfilment/useUnlinkTaxPayerFromFulfilment";
import routes from "../../../../../core/commons/routes";
import { ActivityStatus } from "../../../../../core/dtos/ActivityStatus";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";
import { usePermission } from "../../../../../core/permissions/usePermission";
import { isFiscalCode } from "../../../../../utils/taxPayer";
import { ActivityCounter } from "../../../../components/activity-counter/ActivityCounter";
// Temporarily hidden from the UI (see FulfilmentOperatorAvatar usage below):
// the operator avatar let users associate an operator, but the current UX was
// confusing. The "operator" feature will be reworked in an upcoming refactoring,
// so we keep the component and its APIs in place and only hide the entry point.
// import { FulfilmentOperatorAvatar } from "../fulfilment-operator-avatar/FulfilmentOperatorAvatar";

export interface FulfilmentTaxPayerListItemProps {
    selectedFulfilment: FulfilmentDto;
    taxPayer: TaxPayerDetailsDto;
}

export const FulfilmentTaxPayerListItem = ({
    // Temporarily not destructured: only consumed by the hidden
    // FulfilmentOperatorAvatar below. Kept in the props interface so callers
    // keep passing it and it can be restored with the operator refactoring.
    // selectedFulfilment,
    taxPayer,
}: FulfilmentTaxPayerListItemProps): ReactElement => {
    // Temporarily unused: operators are only consumed by the hidden
    // FulfilmentOperatorAvatar below.
    // const { data: availableOperators, isFetching: isLoadingOperators } =
    //     useGetOperators();

    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showTextMessage } = useSuccessNotification();

    // The row's kebab menu holds a single item, "unlink": with the permission
    // denied both the menu and the button that opens it are pointless.
    const canUnlinkTaxPayer = usePermission("taxPayerUnlink");
    const canLinkTaxPayer = usePermission("taxPayerLink");

    const [isLinked, setIsLinked] = useState(true);

    const [menuAnchorEl, setMenuAnchorEl] = useState<
        (EventTarget & HTMLButtonElement) | null
    >(null);

    const onClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(null);
    };

    // use cases
    const linkTaxPayerMutation = useLinkTaxPayerToFulfilment();
    const unlinkTaxPayerMutation = useUnlinkTaxPayerFromFulfilment();

    const linkTaxPayerCB = useCallback(
        (taxPayer: TaxPayerDetailsDto) => {
            const linkTaxPayerAsync = async () => {
                try {
                    await linkTaxPayerMutation.mutateAsync({
                        taxPayerWorkspaceId: taxPayer.workspaceId,
                        levyId: taxPayer.levyId,
                        year: dayjs(taxPayer.deadline).year(),
                    });
                } catch {
                    return;
                }
                setIsLinked(true);
                showTextMessage(
                    t(
                        "views.home.components.fulfilment_tax_payer_list_item.menu_items.link.successfull",
                    ),
                );
            };
            linkTaxPayerAsync();
        },
        [linkTaxPayerMutation, showTextMessage, t],
    );

    const unlinkTaxPayerCB = useCallback(
        (taxPayer: TaxPayerDetailsDto) => {
            const unlinkTaxPayerAsync = async () => {
                try {
                    await unlinkTaxPayerMutation.mutateAsync({
                        taxPayerWorkspaceId: taxPayer.workspaceId,
                        levyId: taxPayer.levyId,
                        year: dayjs(taxPayer.deadline).year(),
                    });
                } catch {
                    return;
                }
                setIsLinked(false);
                showTextMessage(
                    t(
                        "views.home.components.fulfilment_tax_payer_list_item.menu_items.unlink.successfull",
                    ),
                );
                setMenuAnchorEl(null);
            };
            unlinkTaxPayerAsync();
        },
        [showTextMessage, t, unlinkTaxPayerMutation],
    );

    return (
        <>
            {canUnlinkTaxPayer && (
                <Menu
                    id="menu"
                    anchorEl={menuAnchorEl}
                    open={!!menuAnchorEl}
                    onClose={handleCloseMenu}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <MenuItem
                        disabled={unlinkTaxPayerMutation.isPending}
                        onClick={() => {
                            unlinkTaxPayerCB(taxPayer);
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="center"
                            spacing={1}
                        >
                            <Typography>
                                {t(
                                    "views.home.components.fulfilment_tax_payer_list_item.menu_items.unlink",
                                )}
                            </Typography>
                            {unlinkTaxPayerMutation.isPending && (
                                <CircularProgress
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                    size={16}
                                />
                            )}
                        </Stack>
                    </MenuItem>
                </Menu>
            )}

            <Stack
                alignItems="center"
                direction="row"
                gap={2}
                sx={{ px: 2, py: 1 }}
                width="100%"
            >
                <Box
                    sx={{
                        width: 16,
                        display: "flex",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    {taxPayer.source === TaxPayerSource.TAX_TINDER && (
                        <Tooltip
                            title={t(
                                "views.home.components.fulfilment_tax_payer_list_item.tax_tinder_suggestion.tooltip",
                            )}
                        >
                            <FontAwesomeIcon
                                icon={faSparkles}
                                color={theme.palette.primary.textTitleColor}
                            />
                        </Tooltip>
                    )}
                </Box>
                <ListItemText
                    primary={
                        <Typography
                            sx={{
                                color: theme.palette.text.primary,
                            }}
                            variant="bodyLarge"
                        >
                            {taxPayer.businessName
                                ? taxPayer.businessName
                                : `${taxPayer.name} ${taxPayer.surname}`}
                        </Typography>
                    }
                    secondary={
                        <Typography
                            sx={{
                                color: theme.palette.primary.textSubduedColor,
                            }}
                            variant="body"
                        >
                            {isFiscalCode(
                                taxPayer.taxIdentifier.toUpperCase(),
                            ) ? (
                                <>
                                    {t(
                                        "views.home.components.taxPayer.tax_identifier.taxId",
                                        {
                                            taxId: taxPayer.taxIdentifier.toUpperCase(),
                                        },
                                    )}
                                </>
                            ) : (
                                <>
                                    {t(
                                        "views.home.components.taxPayer.tax_identifier.vatId",
                                        {
                                            vatId:
                                                taxPayer.taxIdentifier.toUpperCase() ||
                                                taxPayer.vatIdentifier.toUpperCase(),
                                        },
                                    )}
                                </>
                            )}
                        </Typography>
                    }
                />
                {!isLinked ? (
                    canLinkTaxPayer && (
                        <Button
                            disabled={linkTaxPayerMutation.isPending}
                            onClick={(e) => {
                                linkTaxPayerCB(taxPayer);
                                handleCloseMenu(e);
                            }}
                            variant="outlined"
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <Typography ml={1}>
                                {t(
                                    "views.home.components.fulfilment_tax_payer_list_item.menu_items.link",
                                    "Associa",
                                )}
                            </Typography>
                            {linkTaxPayerMutation.isPending && (
                                <CircularProgress
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                    size={16}
                                />
                            )}
                        </Button>
                    )
                ) : (
                    <>
                        <Stack alignItems="center" direction="row" gap={1}>
                            {/* Temporarily hidden: the operator avatar allowed
                                users to associate an operator to the fulfilment,
                                but it was confusing in its current form. The
                                "operator" feature will be reworked in an upcoming
                                refactoring; the component and its APIs are kept
                                so it can be restored/replaced at that point. */}
                            {/* <FulfilmentOperatorAvatar
                                availableOperators={availableOperators || []}
                                currentOperator={taxPayer.operator}
                                isLoading={isLoadingOperators}
                                selectedFulfilment={selectedFulfilment}
                                taxPayerWorkspaceId={taxPayer.workspaceId}
                            /> */}
                            <ActivityCounter
                                hasWarnings={taxPayer.withWarning}
                                status={
                                    ActivityStatus[
                                        taxPayer.status as keyof typeof ActivityStatus
                                    ]
                                }
                            />
                            {canUnlinkTaxPayer && (
                                <IconButton
                                    aria-controls="menu"
                                    aria-haspopup="true"
                                    color="primary"
                                    disabled={
                                        taxPayer.status !==
                                        FulfilmentStatus.LINKED
                                    }
                                    onClick={(e) => {
                                        onClickMenu(e);
                                    }}
                                    size="small"
                                >
                                    <FontAwesomeIcon
                                        icon={faEllipsisVertical}
                                        size="xs"
                                    />
                                </IconButton>
                            )}
                        </Stack>
                        <IconButton
                            edge="end"
                            onClick={() =>
                                navigate({
                                    pathname: routes.fulfilmentDetail(),
                                    search: createSearchParams({
                                        taxPayerWorkspaceId:
                                            taxPayer.workspaceId,
                                        levyId: taxPayer.levyId,
                                        year: dayjs(taxPayer.deadline)
                                            .year()
                                            .toString(),
                                    }).toString(),
                                })
                            }
                        >
                            <FontAwesomeIcon
                                icon={faArrowRight}
                                color={theme.palette.primary.interactiveDefault}
                            />
                        </IconButton>
                    </>
                )}
            </Stack>
        </>
    );
};
